/**
 * InkCat mark: THE mascot (PRD §5.5, admitted to canon 2026-06-11).
 *
 * The same archetypal "draw a cat" doodle the splash hero animates, head
 * path, eyes, nose, whiskers are the hero's EXACT stroke data, cropped to a
 * tight viewBox, so every appearance reads as the one cat. Static poses only
 * (the §5.5 rules: sparing, one per screen, aria-hidden, never animated):
 *
 *  - "peek": eyes open, watching. Empty lobby, spectator note.
 *  - "sleepy": eyes closed. The 404 page, the game-over corner.
 *
 * `currentColor` strokes so each surface tints it (usually --ink-soft, so it
 * stays a margin doodle rather than content).
 */

type InkCatProps = {
  pose?: "peek" | "sleepy";
  /** Rendered width in px; height follows the viewBox aspect (132:106). */
  size?: number;
};

/** Head outline: byte-identical to the splash hero's cat head path. */
const HEAD =
  "M 70 132 C 64 104 70 84 90 78 C 86 66 86 58 92 52 C 100 60 104 64 110 66 C 120 64 130 64 140 66 C 146 64 150 60 158 52 C 164 58 164 66 160 78 C 180 84 186 104 180 132 C 160 140 90 140 70 132 Z";

export function InkCat({ pose = "peek", size = 56 }: InkCatProps) {
  const height = Math.round((size * 106) / 132);
  const stroke = {
    "fill": "none",
    "stroke": "currentColor",
    "stroke-width": "2.5",
    "stroke-linecap": "round" as const,
    "stroke-linejoin": "round" as const,
  };
  return (
    <svg
      class="mark-ink-cat"
      viewBox="60 42 132 106"
      width={size}
      height={height}
      aria-hidden="true"
      data-pose={pose}
    >
      <path d={HEAD} {...stroke} />
      {pose === "peek" ? (
        <>
          {/* Eyes open: the hero's upward arcs. */}
          <path d="M 104 98 q 4 -6 8 0" {...stroke} />
          <path d="M 138 98 q 4 -6 8 0" {...stroke} />
        </>
      ) : (
        <>
          {/* Eyes closed: the same arcs flipped downward. */}
          <path d="M 104 96 q 4 5 8 0" {...stroke} />
          <path d="M 138 96 q 4 5 8 0" {...stroke} />
        </>
      )}
      {/* Nose + whiskers: hero stroke data verbatim. */}
      <path d="M 122 110 l 6 6 l 6 -6" {...stroke} />
      <path d="M 96 116 l -22 -4 M 96 122 l -22 6" {...stroke} />
      <path d="M 154 116 l 22 -4 M 154 122 l 22 6" {...stroke} />
    </svg>
  );
}
